import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from '@store/auth/authSlice';
import { Box, Checkbox, FormControlLabel, Typography, CircularProgress } from '@mui/material';
import { BaseDialog } from '../common/BaseDialog';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';
import { adminUsersService } from '../../services/admin-users.service';
import { toastService } from '../../services/toast.service';

export const ROLE_LABELS: Record<string, string> = {
  client: 'Клієнт',
  expert: 'Експерт',
  admin: 'Адміністратор',
  superadmin: 'Суперадмін',
  developer: 'Розробник',
  // 2026-09-29: службові функції для тестування в застосунку й вебі; видається клієнту чи експерту, незалежна від developer.
  tester: 'Тестувальник',
  // 2026-09-30: внутрішні фінанси (старі фінанси, далі - функції для інвесторів). Видає/забирає лише власник.
  owner: 'Власник',
  // 2026-10-06: самостійна роль з власною панеллю (/pm/...), за аналогією з адмінською.
  pm: 'Продакт-менеджер',
};

export const ROLE_CHIP_COLORS: Record<string, 'success' | 'info' | 'secondary' | 'error' | 'warning' | 'default'> = {
  client: 'success',
  expert: 'info',
  admin: 'secondary',
  superadmin: 'error',
  developer: 'warning',
  tester: 'default',
  owner: 'error',
  pm: 'secondary',
};

export const ALL_ROLES = Object.keys(ROLE_LABELS);

interface Props {
  open: boolean;
  onClose: () => void;
  userId: number | null;
  userName: string;
  roles: string[];
  onChanged: (userId: number, roles: string[]) => void;
  // Список ролей, які показувати в модалці — за замовчуванням усі (як на /admin/users/all).
  // Сторінки, звужені до однієї категорії користувачів (напр. /admin/users/clients — лише users
  // з роллю 'client'), передають вужчий список: 'client'/'expert' там нема сенсу пропонувати —
  // 'client' завжди вже активна (сама умова потрапляння в список), а 'expert' заборонена разом з
  // 'client' на бекенді (client і expert не поєднуються в одному акаунті, 2026-08-26).
  visibleRoles?: string[];
}

export const AdminUserRolesModal: React.FC<Props> = ({ open, onClose, userId, userName, roles, onChanged, visibleRoles }) => {
  const [pendingRole, setPendingRole] = useState<string | null>(null);
  const rolesToShow = visibleRoles ?? ALL_ROLES;
  const currentUser = useSelector(selectUser);
  // Роль "Власник" змінює лише власник (бекенд теж перевіряє) - іншим чекбокс неактивний.
  const isOwner = (currentUser?.roles || []).map(r => r.toLowerCase()).includes('owner');

  const handleToggle = async (role: string, active: boolean) => {
    if (!userId) return;
    setPendingRole(role);
    try {
      const updated = await adminUsersService.updateUserRole(userId, role, active);
      onChanged(userId, updated);
      toastService.success(active ? `Роль "${ROLE_LABELS[role]}" видано` : `Роль "${ROLE_LABELS[role]}" відкликано`);
    } catch (error: any) {
      toastService.error(error?.response?.data?.message || 'Не вдалося змінити роль');
    } finally {
      setPendingRole(null);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title="Керування ролями"
      maxWidth="xs"
      iDoc={ModalIndices.ADMIN_USER_ROLES}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {userName}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {rolesToShow.map((role) => (
          <FormControlLabel
            key={role}
            control={
              pendingRole === role ? (
                <Box sx={{ width: 42, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={18} />
                </Box>
              ) : (
                <Checkbox
                  checked={roles.includes(role)}
                  onChange={(e) => handleToggle(role, e.target.checked)}
                  disabled={pendingRole !== null || (role === 'owner' && !isOwner)}
                />
              )
            }
            label={ROLE_LABELS[role]}
          />
        ))}
      </Box>
    </BaseDialog>
  );
};
